import { VariantProps, cva } from 'class-variance-authority';
import { DetailedHTMLProps, ForwardedRef, InputHTMLAttributes, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

const inputVariants = cva(
    [
        'flex items-center',
        'w-full',
        'font-medium',
        'bg-transparent border border-grayscale-300 ',
        'placeholder:text-grayscale-400',
        'focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10',
    ],
    {
        variants: {
            size: {
                sm: ['text-sm px-4 h-8 rounded-md'],
                normal: ['text-sm px-4 h-9 rounded-md'],
                lg: ['text-base px-[18px] h-10 rounded-md'],
            },
            invalid: {
                true: ['border-danger-500 outline outline-danger-50 focus:border-danger-500 focus:outline-danger-50'],
                false: [],
            },
            valid: {
                true: [
                    'border-success-500 outline outline-success-50 focus:border-success-500 focus:outline-success-50',
                ],
                false: [],
            },
            disabled: {
                true: ['bg-[#FAFAFA] dark:bg-[#20262D] opacity-70'],
                false: [],
            },
        },
        defaultVariants: {
            size: 'normal',
            disabled: false,
            invalid: false,
            valid: false,
        },
    }
);

export interface InputProps
    extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'size'>,
        VariantProps<typeof inputVariants> {
    ref?: ForwardedRef<HTMLInputElement>;
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const { size, invalid, valid, disabled, className, ...rest } = props;

    return (
        <input
            {...rest}
            ref={ref}
            disabled={disabled}
            className={twMerge(inputVariants({ size, invalid, valid, disabled, className }))}
        />
    );
});

export default Input;
